import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';

export type DownloadTarget =
  | { mode: 'saveFile'; fileHandle: any }
  | { mode: 'browser' };

export type SaveFileSelectionResult =
  | { status: 'selected'; handle: any }
  | { status: 'cancelled' }
  | { status: 'permissionDenied'; message: string }
  | { status: 'unsupported'; message: string }
  | { status: 'error'; message: string };

export type BlobSaveResult =
  | { status: 'saved' }
  | { status: 'cancelled'; message: string }
  | { status: 'permissionDenied'; message: string }
  | { status: 'writeFailed'; message: string };

@Injectable({
  providedIn: 'root'
})
export class DownloadService {
  private progressSubject = new Subject<any>();
  downloadObj: any;
  isDownloading: boolean = false; 
  private isDownloadingSubject = new BehaviorSubject<boolean>(false);
  isDownloading$ = this.isDownloadingSubject.asObservable();
  private progressSubscription: Subscription | null = null; // Store subscription

  setProgress(sensorObj: any) {
    this.downloadObj = { progress: sensorObj.progress , sensorId: sensorObj?.sensorId, sceneId: sensorObj?.sceneId, taskId: sensorObj?. taskId, downloadClick: sensorObj?.download, product:sensorObj?.product,
      action: sensorObj?.action, type: sensorObj?.type
    };
    this.progressSubject.next(this.downloadObj);   
    this.isDownloadingSubject.next(true); 
  }

  getProgress(): any {
    return this.downloadObj;
  }

  getProgressObservable() {
    return this.progressSubject.asObservable();
    this.isDownloadingSubject.next(false);
  }
  setDownloadingStatus(isDownloading: boolean) {
    this.isDownloading = isDownloading;
  }

  unsubscribeProgress() {
    if (this.progressSubscription) {
      this.progressSubscription.unsubscribe();
      this.progressSubscription = null;
    }
  
    // Reset progress to stop showing the spinner
    this.progressSubject.next({ progress: 200, downloadClick: false });
    this.isDownloadingSubject.next(false);
  }

  isFolderPickerSupported(): boolean {
    const picker = (window as any).showSaveFilePicker;
    return window.isSecureContext && typeof picker === 'function';
  }

  async pickSaveFileForDownload(
    fileName: string
  ): Promise<SaveFileSelectionResult> {
    if (!this.isFolderPickerSupported()) {
      return {
        status: 'unsupported',
        message:
          'File save picker is not available in this browser context. Please use a supported secure browser.',
      };
    }

    try {
      const normalizedFileName = this.normalizeSuggestedFileName(fileName);
      const extension = this.getFileExtension(normalizedFileName);
      const fileHandle = await (window as any).showSaveFilePicker({
        suggestedName: normalizedFileName,
        id: 'eartheye-downloads',
        startIn: 'downloads',
        types: this.getPickerFileTypes(extension),
      });
      return { status: 'selected', handle: fileHandle };
    } catch (error: any) {
      const errorName = error?.name ?? '';

      if (
        errorName === 'AbortError' ||
        errorName === 'InvalidStateError' ||
        errorName === 'NotFoundError'
      ) {
        return { status: 'cancelled' };
      }

      if (errorName === 'NotAllowedError' || errorName === 'SecurityError') {
        return {
          status: 'permissionDenied',
          message:
            'Browser permission prevented selecting this file location. Please try another location.',
        };
      }

      return {
        status: 'error',
        message:
          'Unable to access the selected file location. Please try again.',
      };
    }
  }

  async saveBlob(
    blob: Blob,
    fileName: string,
    target: DownloadTarget
  ): Promise<BlobSaveResult> {
    if (target?.mode === 'browser') {
      return this.triggerBrowserDownload(blob, fileName);
    }

    if (!target?.fileHandle) {
      return {
        status: 'writeFailed',
        message:
          'The selected save location is unavailable. Please choose a file location again.',
      };
    }

    try {
      const writable = await target.fileHandle.createWritable();
      await writable.write(blob);
      await writable.close();
      return { status: 'saved' };
    } catch (error: any) {
      const errorName = error?.name ?? '';
      if (errorName === 'AbortError') {
        return {
          status: 'cancelled',
          message: 'File save was cancelled.',
        };
      }

      if (errorName === 'NotAllowedError' || errorName === 'SecurityError') {
        return {
          status: 'permissionDenied',
          message:
            'Browser permission prevented saving this file. Please choose another location.',
        };
      }

      return {
        status: 'writeFailed',
        message:
          'The selected file could not be written. Please try again with another location.',
      };
    }
  }

  private triggerBrowserDownload(
    blob: Blob,
    fileName: string
  ): BlobSaveResult {
    try {
      const normalizedFileName = this.normalizeSuggestedFileName(fileName);
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = normalizedFileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      return { status: 'saved' };
    } catch {
      return {
        status: 'writeFailed',
        message: 'Unable to trigger browser download. Please try again.',
      };
    }
  }

  private hasFileExtension(fileName: string): boolean {
    return /\.[a-z0-9]{1,10}$/i.test(fileName);
  }

  private getFileExtension(fileName: string): string {
    const extensionMatch = fileName.match(/\.([a-z0-9]{1,10})$/i);
    return extensionMatch?.[1]?.toLowerCase() || 'zip';
  }

  private getPickerFileTypes(extension: string): any[] {
    const normalizedExtension = extension.toLowerCase();

    const mimeByExtension: { [key: string]: string } = {
      zip: 'application/zip',
      pdf: 'application/pdf',
      tif: 'image/tiff',
      tiff: 'image/tiff',
      json: 'application/json',
      txt: 'text/plain',
      csv: 'text/csv',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      bin: 'application/octet-stream',
    };

    const mime = mimeByExtension[normalizedExtension] || 'application/octet-stream';
    return [
      {
        description: `${normalizedExtension.toUpperCase()} file`,
        accept: {
          [mime]: [`.${normalizedExtension}`],
        },
      },
    ];
  }

  private normalizeSuggestedFileName(fileName: string): string {
    const fallbackFileName = 'download.zip';

    if (!fileName || typeof fileName !== 'string') {
      return fallbackFileName;
    }

    let normalizedFileName = fileName.trim();

    try {
      normalizedFileName = decodeURIComponent(normalizedFileName);
    } catch {
      // Keep original value when decoding fails.
    }

    normalizedFileName = normalizedFileName.split('#')[0].split('?')[0];
    normalizedFileName = normalizedFileName.replace(/\\/g, '/');
    normalizedFileName = normalizedFileName.split('/').pop() || '';
    normalizedFileName = normalizedFileName
      .replace(/["<>:|*]/g, '')
      .trim();

    if (!normalizedFileName) {
      return fallbackFileName;
    }

    if (!this.hasFileExtension(normalizedFileName)) {
      normalizedFileName = `${normalizedFileName}.zip`;
    }

    return normalizedFileName;
  }
}
